import { useState, useCallback } from 'react';
import { findImage } from '../../../services/pexels.service';
import tripFolderService from '../../../services/trips/trip-folder.service';
import { useTrip } from '../../contexts/trip-context';

export interface IAddFolderModel {
  title: string;
  folderType: string;
  description: string;
  isPublic: boolean;
  parentFolderId: string | null;
  imageUrl: string; // Add imageUrl to the folder model
  placesId?: string;
  latitude?: number;
  longitude?: number;
}

export const useAddFolder = (
  tripId: string,
  parentFolderId: string = '',
  onSuccess?: () => void
) => {
  const [folder, setFolder] = useState<IAddFolderModel>({
    title: '',
    folderType: 'custom', // Default folder type
    description: '',
    isPublic: false,
    parentFolderId: parentFolderId || null, // Use parentFolderId if provided
    imageUrl: 'default.jpg' // Default image
  });
  const [imageUrl, setImageUrl] = useState('');
  const [validated, setValidated] = useState(true);
  const { setRefreshTrip } = useTrip(); // <-- Add this line

  // Fetch image based on a search term
  const fetchImage = useCallback(async (searchText: string) => {
    try {
      const response = await findImage(searchText);
      setFolder((prev) => ({
        ...prev,
        imageUrl: response[0].src.original // Update the imageUrl in the folder state
      }));
    } catch (error) {
      console.error('Error fetching image:', error);
    }
  }, []);

  const handleInputChange = (field: keyof IAddFolderModel, value: any) => {
    setFolder((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      if (folder.folderType !== 'custom') {
        folder.title = folder.folderType;
        folder.description = folder.folderType;
      }
      const success = await tripFolderService.createTripFolder(tripId, folder);
      if (success) {
        alert('Folder created successfully');
        setRefreshTrip(true); // Trigger a refresh of the folder list
        setFolder({
          title: '',
          folderType: '', // Reset to default folder type
          description: '',
          isPublic: false,
          parentFolderId: '',
          imageUrl: 'default.jpg'
        }); // Clear the form after successful save
        onSuccess?.();
      } else {
        alert('Failed to create folder');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  return {
    folder,
    validated,
    fetchImage, // Expose fetchImage to be used in the form
    handleInputChange,
    handleSubmit
  };
};
